"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Chip,
  Button,
  Typography,
  styled,
  CardContent,
  CircularProgress,
  Alert,
  Box,
  Avatar,
} from "@mui/material";
import { useParams } from "react-router-dom";
import useApi from "../../../Hooks/useApi";
import { handleApiError } from "../../../Helpers/ErrorHandler";
import Common from "../../../Components/Layouts/Common";
import BasicTable from "../../../Components/Table/Material";
import ConfirmModal from "../../../Components/Modal/ConfirmModal";

const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(16),
  height: theme.spacing(16),
}));

const UserPermissions = () => {
  const userId = useParams().id;
  const [user, setUser] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const { request, error, loading } = useApi();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [actionType, setActionType] = useState(null); // "grant" | "revoke"

  // Fetch user data
  const fetchUser = useCallback(async () => {
    try {
      const response = await request("GET", `/v1/user/${userId}`);
      setUser(response);
    } catch (error) {
      console.log(error);
    }
  }, [request, userId]);

  // Fetch all available permissions
  const fetchAllPermissions = useCallback(async () => {
    try {
      const response = await request("GET", `/v1/permissions`);
      setAllPermissions(response);
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  }, [request]);

  useEffect(() => {
    fetchUser();
    fetchAllPermissions();
  }, [fetchUser, fetchAllPermissions]);

  const grantPermission = async (permissionId) => {
    try {
      await request("POST", `/v1/rbac/users/permissions`, {
        data: {
          userId: Number.parseInt(userId),
          permissions: [permissionId],
        },
      });
      fetchUser();
    } catch (err) {
      console.error("Error granting permission:", err);
      handleApiError(err);
    }
  };

  const revokePermission = async (permissionId) => {
    try {
      await request("PUT", `/v1/rbac/users/permissions`, {
        data: {
          userId: Number.parseInt(userId),
          permissions: [permissionId],
        },
      });
      fetchUser();
    } catch (err) {
      console.error("Error revoking permission:", err);
      handleApiError(err);
    }
  };

  const handleGrantClick = (permissionId) => {
    setSelectedPermission(permissionId);
    setActionType("grant");
    setIsConfirmModalOpen(true);
  };

  const handleRevokeClick = (permissionId) => {
    setSelectedPermission(permissionId);
    setActionType("revoke");
    setIsConfirmModalOpen(true);
  };

  const handleConfirmAction = () => {
    if (selectedPermission && actionType === "grant") {
      grantPermission(selectedPermission);
    } else if (selectedPermission && actionType === "revoke") {
      revokePermission(selectedPermission);
    }
    setIsConfirmModalOpen(false);
    setSelectedPermission(null);
    setActionType(null);
  };

  const sortedPermissions = [...allPermissions].sort((a, b) => {
    const aGranted = user?.permissions.includes(a.name);
    const bGranted = user?.permissions.includes(b.name);
    if (aGranted && !bGranted) return -1;
    if (!aGranted && bGranted) return 1;
    return 0;
  });

  const columns = [
    {
      id: "name",
      label: "Permission Name",
      minWidth: 170,
      renderCell: (row) => <>{row.name}</>,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 170,
      renderCell: (row) => {
        const isGranted = user?.permissions.includes(row.name);
        return (
          <Chip
            label={isGranted ? "Granted" : "Not Granted"}
            color={isGranted ? "success" : "default"}
            size="small"
          />
        );
      },
    },
    {
      id: "action",
      label: "Action",
      minWidth: 170,
      renderCell: (row) => {
        const isGranted = user?.permissions.includes(row.name);
        return (
          <Button
            variant="outlined"
            color={isGranted ? "error" : "primary"}
            size="small"
            onClick={() =>
              isGranted ? handleRevokeClick(row.id) : handleGrantClick(row.id)
            }
          >
            {isGranted ? "Revoke" : "Grant"}
          </Button>
        );
      },
    },
  ];

  return (
    <Common headline={"Permission Management"}>
      <Box sx={{ maxWidth: 1200, margin: "auto", padding: 0 }}>
        <StyledCard>
          <CardContent>
            <Box gap={2}>
              <Box textAlign={{ xs: "center", md: "left" }}>
                <div className="flex gap-2 items-center w-full">
                  <Typography variant="h4" gutterBottom>
                    {user?.fullName}
                  </Typography>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent={{ xs: "center", md: "flex-start" }}
                    gap={1}
                  >
                    {user?.designation && (
                      <Chip label={user?.designation} variant="outlined" />
                    )}
                    {user?.roles && (
                      <Chip label={user?.roles} variant="outlined" />
                    )}
                    {user?.department && (
                      <Chip label={user?.department} variant="outlined" />
                    )}
                    {
                      <Chip
                        label={user?.status}
                        color={user?.status === "active" ? "success" : "error"}
                      />
                    }
                  </Box>
                </div>

                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {user?.email}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </StyledCard>

        {loading ? (
          <StyledCard>
            <CardContent>
              <CircularProgress />
            </CardContent>
          </StyledCard>
        ) : error ? (
          <StyledCard>
            <CardContent>
              <Alert severity="error">{error.message}</Alert>
            </CardContent>
          </StyledCard>
        ) : (
          <StyledCard>
            <CardContent>
              {allPermissions.length === 0 ? (
                <Alert severity="info">No permissions available.</Alert>
              ) : (
                <BasicTable
                  columns={columns}
                  data={sortedPermissions}
                  maxHeight={1500}
                />
              )}
            </CardContent>
          </StyledCard>
        )}
      </Box>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmAction}
        label={
          actionType === "grant"
            ? "Are you sure you want to grant this permission?"
            : "Are you sure you want to revoke this permission?"
        }
      />
    </Common>
  );
};

export default UserPermissions;
